import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import "../base/pd-base-ui-input.js";
import { PdBaseInputElement } from "../base/pd-base-input-element.js";
import { css, html, nothing } from "lit";
import { property } from "lit/decorators.js";
import "@progressive-development/pd-icon/pd-icon";
import { classMap } from "lit/directives/class-map.js";
import { msg, str } from "@lit/localize";
//#region src/pd-input/PdInput.ts
/**
* @license
* Copyright (c) 2021 PD Progressive Development UG. All rights reserved.
*/
var onlyContainsNumbers = (strVar) => /^\d+$/.test(strVar);
/**
* Text input component for forms.
*
* @tagname pd-input
* @summary Text input with validation, icons, and form integration.
*
* @event input-icon-click - Fired when the input icon is clicked.
* @event validate-form - Fired when input value changes for validation.
* @event field-change - Fired when input value changes.
*
* @cssprop --pd-input-field-width - Input width. Default: `250px`.
* @cssprop --pd-input-field-height - Input height. Default: `2.2rem`.
* @cssprop --pd-input-field-padding - Input padding. Default: `0.25rem`.
* @cssprop --pd-input-field-bg-col - Background color. Default: `var(--pd-default-bg-col)`.
* @cssprop --pd-input-field-border - Border style. Default: `1px solid var(--pd-default-light-col)`.
* @cssprop --pd-input-field-border-bottom - Bottom border. Default: `2px solid var(--pd-default-col)`.
* @cssprop --pd-input-field-border-focus - Focus border. Default: `2px solid var(--pd-default-col)`.
* @cssprop --pd-input-field-border-col-hover - Hover border color. Default: `var(--pd-default-hover-col)`.
* @cssprop --pd-input-field-bg-col-disabled - Disabled background. Default: `#e9e9e9`.
* @cssprop --pd-input-field-border-bottom-disabled - Disabled bottom border. Default: `2px solid var(--pd-default-disabled-col)`.
* @cssprop --pd-input-placeholder-color - Placeholder text color. Default: `#474747e4`.
* @cssprop --pd-input-icon-size - Icon size when using icon attribute. Default: `calc(var(--pd-input-field-height) * 0.9)`.
* @cssprop --pd-input-icon-color - Icon color. Default: `var(--pd-default-col)`.
*/
var PdInput = class extends PdBaseInputElement {
	static {
		this.styles = [PdBaseInputElement.styles, css`
      * {
        box-sizing: border-box;
      }
    `];
	}
	constructor() {
		super();
		this.icon = "";
		this.clickableIcon = false;
		this.iconLabel = "";
		this.secret = false;
		this.maxlength = 500;
		this.onlyNumbers = false;
		this._inputType = 1;
	}
	connectedCallback() {
		this._setupValidators();
		super.connectedCallback();
	}
	_setupValidators() {
		const validators = [];
		if (this.minlength !== void 0) validators.push((value) => value.length < this.minlength ? msg(str`Mindestens ${this.minlength} Zeichen`) : null);
		if (this.maxlength !== void 0) validators.push((value) => value.length > this.maxlength ? msg(str`Maximal ${this.maxlength} Zeichen`) : null);
		this._validators = validators;
	}
	render() {
		const inputId = `${this.id}Input`;
		const errorId = `${this.id}Error`;
		return html`
      ${this._renderLabel(inputId)}
      <div
        class="${classMap(this.getClassmap({ input: true }))}"
      >
        <!-- Feld vor Icon: Das In-Feld-Icon ist absolut positioniert, die
             DOM-Reihenfolge hat also keinen visuellen Effekt — wohl aber
             auf die Tab-Reihenfolge. Stünde das Icon zuerst, landete man
             beim Tabben auf dem Icon (z. B. dem Passwort-Auge), bevor man
             im Eingabefeld ist. -->
        ${this._renderInputField(inputId, errorId)} ${this._renderFieldIcon()}
      </div>
      ${this._renderErrorMsg(errorId)}
    `;
	}
	/**
	* Renders the optional in-field icon. Override in subclasses to provide a
	* custom in-field control (e.g. a password reveal toggle) while reusing the
	* shared `.infield-icon` positioning and the `icon-right`/`icon-left` layout.
	*/
	_renderFieldIcon() {
		if (!this.icon) return nothing;
		return html`
      <pd-icon
        icon="${this.icon}"
        ?activeIcon="${this.clickableIcon}"
        class="infield-icon"
        tabindex="${this.clickableIcon ? 0 : -1}"
        role="${this.clickableIcon ? "button" : "img"}"
        aria-label="${this.clickableIcon ? this.iconLabel : ""}"
        aria-hidden="${!this.clickableIcon}"
        @click="${this.clickableIcon ? this._iconClicked : null}"
        @keydown="${this.clickableIcon ? this._onIconKeyDown : null}"
      ></pd-icon>
    `;
	}
	/** Renders the input field. Override in subclasses to add additional attributes. */
	_renderInputField(inputId, errorId) {
		return html`
      <input
        id="${inputId}"
        name="${this.name || this.valueName || this.autoCompleteName}"
        autocomplete="${this.autoCompleteName ?? "off"}"
        class="input-style ${this.gradient ? "gradient" : ""}"
        type="${this.secret ? "password" : "text"}"
        placeholder="${this.placeHolder}"
        .value="${this._value}"
        minlength="${this.minlength ?? ""}"
        maxlength="${this.maxlength ?? ""}"
        ?readonly="${this.readonly}"
        ?disabled="${this.disabled}"
        aria-invalid="${this._invalid}"
        aria-describedby="${this._errorMsg ? errorId : ""}"
        @keyup="${this._onKeyUp}"
        @blur="${this._onBlur}"
        @focus="${this._onFocus}"
      />
    `;
	}
	_onKeyUp(e) {
		if (this.onlyNumbers && this._input?.value && !onlyContainsNumbers(this._input.value)) this._input.value = this._input.value.replace(/\D/g, "");
		super._onKeyUp(e);
	}
	_iconClicked() {
		this.dispatchEvent(new CustomEvent("input-icon-click", {
			bubbles: true,
			composed: true
		}));
	}
	_onIconKeyDown(event) {
		if (event.key === " " || event.key === "Enter") {
			event.preventDefault();
			this._iconClicked();
		}
	}
};
__decorate([property({ type: String })], PdInput.prototype, "icon", void 0);
__decorate([property({ type: Boolean })], PdInput.prototype, "clickableIcon", void 0);
__decorate([property({ type: String })], PdInput.prototype, "iconLabel", void 0);
__decorate([property({ type: Boolean })], PdInput.prototype, "secret", void 0);
__decorate([property({ type: Number })], PdInput.prototype, "minlength", void 0);
__decorate([property({ type: Number })], PdInput.prototype, "maxlength", void 0);
__decorate([property({ type: Boolean })], PdInput.prototype, "onlyNumbers", void 0);
//#endregion
export { PdInput };
